import type { ViewFieldsFragment } from '@salescore/client-api'
import type {
  CompiledFormViewConfig,
  CompiledKpiPivotViewConfig,
  CompiledKpiViewConfig,
  CompiledSheetViewConfig,
  ViewUiFormComponent,
} from '@salescore/core'
import type { ReactElement } from 'react'
import * as React from 'react'

import type { SalescoreObjectAbility, ViewAbility } from '../../recoil/view/atoms'
import { ViewUICheckboxC } from './ViewUICheckbox'
import { ViewUIChildRecordsFormC } from './ViewUIChildRecordsForm'
import { ViewUIColC } from './ViewUICol'
import { ViewUIDatePickerC } from './ViewUIDatePicker'
import { ViewUIFormC } from './ViewUIForm'
import { ViewUIFormItemC } from './ViewUIFormItem'
import { ViewUIFormListC } from './ViewUIFormList'
import { ViewUIInputC } from './ViewUIInput'
import { ViewUIInputNumberC } from './ViewUIInputNumber'
import { ViewUIKpi } from './ViewUIKpi'
import { ViewUIKpiPivot } from './ViewUIKpiPivot'
import { ViewUIRowC } from './ViewUIRow'
import { ViewUISearchSelectC } from './ViewUISearchSelect'
import { ViewUISelectC } from './ViewUISelect'
import { ViewUISheetWithNavigator } from './ViewUISheetWithNavigator'
import { ViewUITitleFormItemC } from './ViewUITitleFormItem'

export const ViewUIComponentC = ({
  compiledConfig,
  view,
  setView,
  ability,
  hasChangeToKpiPivotParameter,
  setHasChangeToKpiPivotParameter,
  hasChangeToViewSchema,
  setHasChangeToViewSchema,
}: {
  compiledConfig: CompiledSheetViewConfig | CompiledKpiViewConfig | CompiledKpiPivotViewConfig | CompiledFormViewConfig
  view: ViewFieldsFragment
  setView: (f: (previous: ViewFieldsFragment) => ViewFieldsFragment) => void
  ability: ViewAbility & SalescoreObjectAbility
  hasChangeToKpiPivotParameter: boolean
  setHasChangeToKpiPivotParameter: (hasChangeToKpiPivotParameter: boolean) => void
  hasChangeToViewSchema: boolean
  setHasChangeToViewSchema: (hasChangeToViewSchema: boolean) => void
}): ReactElement => {
  switch (compiledConfig.type) {
    case 'sheet': {
      return (
        <ViewUISheetWithNavigator
          compiledConfig={compiledConfig}
          view={view}
          setView={setView}
          ability={ability}
          hasChangeToViewSchema={hasChangeToViewSchema}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )
    }
    case 'kpiPivot': {
      return (
        <ViewUIKpiPivot
          compiledConfig={compiledConfig}
          view={view}
          ability={ability}
          hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
          setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )
    }
    case 'kpi': {
      return (
        <ViewUIKpi
          compiledConfig={compiledConfig}
          view={view}
          ability={ability}
          hasChangeToKpiPivotParameter={hasChangeToKpiPivotParameter}
          setHasChangeToKpiPivotParameter={setHasChangeToKpiPivotParameter}
          setHasChangeToViewSchema={setHasChangeToViewSchema}
        />
      )
    }
    case 'form': {
      return <ViewUIFormC compiledConfig={compiledConfig} view={view} ability={ability} />
    }
  }
}

export const ViewUIFormComponentC = ({
  component,
  compiledConfig,
  showError = false,
  view,
  ability,
}: {
  component: ViewUiFormComponent
  compiledConfig: CompiledFormViewConfig
  showError?: boolean
  view: ViewFieldsFragment
  ability: ViewAbility & SalescoreObjectAbility
}): ReactElement => {
  const { type } = component
  switch (type) {
    // Grid
    case 'Row': {
      return (
        <ViewUIRowC
          component={component}
          compiledConfig={compiledConfig}
          showError={showError}
          view={view}
          ability={ability}
        />
      )
    }
    case 'Col': {
      return (
        <ViewUIColC
          component={component}
          compiledConfig={compiledConfig}
          showError={showError}
          view={view}
          ability={ability}
        />
      )
    }

    // Form
    case 'FormList': {
      return (
        <ViewUIFormListC
          component={component}
          compiledConfig={compiledConfig}
          showError={showError}
          view={view}
          ability={ability}
        />
      )
    }
    case 'ChildRecordsForm': {
      return (
        <ViewUIChildRecordsFormC
          component={component}
          compiledConfig={compiledConfig}
          showError={showError}
          view={view}
          ability={ability}
        />
      )
    }
    case 'FormItem': {
      return (
        <ViewUIFormItemC
          component={component}
          compiledConfig={compiledConfig}
          showError={showError}
          view={view}
          ability={ability}
        />
      )
    }
    case 'TitleFormItem': {
      return <ViewUITitleFormItemC component={component} />
    }

    // Data Entry
    case 'Input': {
      return <ViewUIInputC component={component} />
    }
    case 'Select': {
      return <ViewUISelectC component={component} view={view} />
    }
    case 'SearchSelect': {
      return <ViewUISearchSelectC component={component} />
    }
    case 'InputNumber': {
      return <ViewUIInputNumberC component={component} />
    }
    case 'DatePicker': {
      return <ViewUIDatePickerC component={component} />
    }
    case 'Checkbox': {
      return <ViewUICheckboxC component={component} />
    }
  }
}

//
// [deprecated] recoilに移行予定
//
export const ViewUIContext = React.createContext<{
  isEditMode: boolean
  onFinish?: (argument?: { id?: string }) => void
}>({
  isEditMode: false,
  onFinish: undefined,
})
